import {
  Button,
  makeStyles,
  TextField,
  TextFieldProps,
  Typography,
} from '@material-ui/core'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useEffect, useMemo, useState } from 'react'
import {
  MountpointInfo,
  SubmitState,
  useConfirmPosition,
  useTrashMountpointInfo,
  useUpdateMountpointInfo,
} from '../api'
import { strings } from '../strings'
import { MountpointGroupMultiSelector } from './MountpointGroupMultiSelector'
import { useHistory } from 'react-router-dom'
import { ConfirmButton } from '../ConfirmButton'

interface MountpointInfoFormProps {
  mountpointInfo: MountpointInfo
}

export function MountpointInfoForm(props: MountpointInfoFormProps) {
  const classes = useStyles()
  const history = useHistory()
  const updateMountpointInfo = useUpdateMountpointInfo()
  const trashMountpointInfo = useTrashMountpointInfo()
  const { mountpointInfo: info } = props

  const confirmPosition = useConfirmPosition()

  const { register, handleSubmit, setValue, reset, watch, control } = useForm()
  const [submitState, setSubmitState] = useState(SubmitState.None)
  const sourceType = watch('sourceType', info.sourceType)

  const onSubmit: SubmitHandler<MountpointInfo> = (formData) => {
    setSubmitState(SubmitState.Submitting)
    const temp = { ...info, ...formData } as any
    temp.altitude = +temp.altitude.toFixed(2)
    temp.latitude = +temp.latitude.toFixed(7)
    temp.longitude = +temp.longitude.toFixed(7)

    delete temp.rtcm_latitude
    delete temp.rtcm_longitude
    delete temp.rtcm_altitude
    delete temp.coord_alert

    updateMountpointInfo(temp)
      .then(() => {
        setSubmitState(SubmitState.Success)
        reset(formData)

        if (info.coord_alert) confirmPosition(info.id)
      })
      .catch((err) => {
        console.log(err.message)
        setSubmitState(SubmitState.Error)
      })
  }

  const handleDelete = () => {
    setSubmitState(SubmitState.Submitting)
    trashMountpointInfo(info.id)
      .then(() => {
        history.replace('/mountpoints')
      })
      .catch((err) => {
        console.log(err)
        setSubmitState(SubmitState.Error)
      })
  }

  useEffect(() => {
    if (info)
      reset({
        ...info,
        altitude: +info.altitude.toFixed(2),
        latitude: +info.latitude.toFixed(7),
        longitude: +info.longitude.toFixed(7),
      })
  }, [info])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={classes.fields}>
        <Typography className={classes.sectionTitle1}>
          {strings.mainFields}
        </Typography>
        <div className={classes.section1}>
          <CustomTextField
            name="name"
            label={strings.name}
            defaultValue={info.name}
            inputRef={register}
            fullWidth={false}
          />
          <InactiveSelect
            name="inactive"
            label={`${strings.active}/${strings.inactive}`}
            defaultValue={info.inactive}
            inputRef={register({ setValueAs: (v) => v === 'true' })}
          />

          <CustomTextField
            name="latitude"
            label={strings.latitude}
            defaultValue={info.latitude}
            inputRef={register({ valueAsNumber: true })}
            style={{ gridColumnStart: 1 }}
            fullWidth={false}
            type="number"
            inputProps={{ step: 'any' }}
          />
          <CustomTextField
            name="rtcm_latitude"
            label={`${strings.latitude} ${strings.base}`}
            value={+info.rtcm_latitude.toFixed(7)}
            fullWidth={false}
            type="number"
            inputProps={{ step: 'any' }}
            disabled
          />

          <CustomTextField
            name="longitude"
            label={strings.longitude}
            defaultValue={info.longitude}
            inputRef={register({ valueAsNumber: true })}
            fullWidth={false}
            type="number"
            inputProps={{ step: 'any' }}
          />
          <CustomTextField
            name="rtcm_longitude"
            label={`${strings.longitude} ${strings.base}`}
            value={+info.rtcm_longitude.toFixed(7)}
            fullWidth={false}
            type="number"
            inputProps={{ step: 'any' }}
            disabled
          />

          <CustomTextField
            name="altitude"
            label={strings.altitude}
            defaultValue={info.altitude}
            inputRef={register({ valueAsNumber: true })}
            fullWidth={false}
            type="number"
            inputProps={{ step: 'any' }}
          />
          <CustomTextField
            name="rtcm_altitude"
            label={`${strings.altitude} ${strings.base}`}
            value={+info.rtcm_altitude.toFixed(2)}
            fullWidth={false}
            type="number"
            inputProps={{ step: 'any' }}
            disabled
          />

          {info.coord_alert && (
            <Button
              onClick={() => {
                setValue('altitude', +info.rtcm_altitude.toFixed(2))
                setValue('latitude', +info.rtcm_latitude.toFixed(7))
                setValue('longitude', +info.rtcm_longitude.toFixed(7))
              }}
              variant="outlined"
              color="primary"
              fullWidth
              style={{ gridColumn: '1/3' }}
            >
              {strings.useBasePosition}
            </Button>
          )}

          <MountpointGroupMultiSelector
            label={strings.groups}
            name="groupIds"
            creator={true}
            control={control}
            defaultValue={info.groupIds}
            style={{ gridColumn: '1/3' }}
          />
        </div>
        <Typography className={classes.sectionTitle}>
          {strings.mountpointSource}
        </Typography>
        <SourceTypeSelect
          name="sourceType"
          label={strings.sourceType}
          defaultValue={info.sourceType}
          inputRef={register}
        />
        <CustomTextField
          name="serverPassword"
          label={strings.serverPassword}
          inputRef={register}
          style={{ display: sourceType === 'server' ? 'flex' : 'none' }}
        />
        <MountpointGroupMultiSelector
          name="sourceNearestGroupIds"
          label={strings.sourceNearestNames}
          creator={false}
          control={control}
          defaultValue={info.sourceNearestGroupIds}
          style={{ display: sourceType === 'nearest' ? 'flex' : 'none' }}
        />
        <CustomTextField
          name="sourceExternalAddr"
          label={strings.sourceExternalAddr}
          defaultValue={info.sourceExternalAddr}
          inputRef={register}
          style={{
            display: ['external', 'proxy'].includes(sourceType)
              ? 'flex'
              : 'none',
          }}
        />
        <CustomTextField
          name="sourceExternalUsername"
          label={strings.sourceExternalUsername}
          defaultValue={info.sourceExternalUsername}
          inputRef={register}
          style={{
            display: ['external', 'proxy'].includes(sourceType)
              ? 'flex'
              : 'none',
          }}
        />
        <CustomTextField
          name="sourceExternalPassword"
          label={strings.sourceExternalPassword}
          defaultValue={info.sourceExternalPassword}
          inputRef={register}
          style={{
            display: ['external', 'proxy'].includes(sourceType)
              ? 'flex'
              : 'none',
          }}
        />
        <CustomTextField
          name="sourceExternalMountpoint"
          label={strings.sourceExternalMountpoint}
          defaultValue={info.sourceExternalMountpoint}
          inputRef={register}
          style={{
            display: ['external', 'proxy'].includes(sourceType)
              ? 'flex'
              : 'none',
          }}
        />
        <Typography className={classes.sectionTitle}>
          {strings.supplementaryFields}
        </Typography>
        <CustomTextField
          name="identifier"
          label={strings.identifier}
          defaultValue={info.identifier}
          inputRef={register}
        />
        <CustomSelect
          name="format"
          label={strings.format}
          defaultValue={info.format}
          inputRef={register}
        >
          <option>RTCM 3.2</option>
          <option>RTCM 3.1</option>
          <option>RTCM 3</option>
          <option>RTCM 2.3</option>
          <option>RAW</option>
        </CustomSelect>
        <CustomSelect
          name="formatDetails"
          label={strings.formatDetails}
          defaultValue={info.formatDetails}
          inputRef={register}
        >
          <option>1004(1),1005(10),MSM4(1)</option>
          <option>1004(1),1006(10),1008(10),1012(1)</option>
          <option>1(1),3(10),18(1),19(1),22(10)</option>
        </CustomSelect>
        <CustomSelect
          name="carrier"
          label={strings.carrier}
          defaultValue={info.carrier}
          inputRef={register}
        >
          <option>DGPS</option>
          <option>L1</option>
          <option>L1&L2</option>
        </CustomSelect>
        <CustomSelect
          name="navigationSystem"
          label={strings.navigationSystem}
          defaultValue={info.navigationSystem}
          inputRef={register}
        >
          <option>GPS</option>
          <option>GPS+GLONASS</option>
          <option>GPS+GLONASS+BAIDOU</option>
          <option>GPS+GLONASS+BAIDOU+GALILEO</option>
        </CustomSelect>
        <CustomTextField
          name="network"
          label={strings.network}
          defaultValue={info.network}
          inputRef={register}
        />
        <CustomTextField
          name="country"
          label={strings.country}
          defaultValue={info.country}
          inputRef={register}
        />
        <CustomSelect
          name="nmea"
          label={strings.nmea}
          defaultValue={info.nmea}
          inputRef={register}
        >
          <option>NO</option>
          <option>YES</option>
        </CustomSelect>
        <CustomSelect
          name="solution"
          label={strings.solution}
          defaultValue={info.solution}
          inputRef={register}
        >
          <option>Single Base</option>
          <option>Network</option>
        </CustomSelect>
        <CustomTextField
          name="generator"
          label={strings.generator}
          defaultValue={info.generator}
          inputRef={register}
        />
        <CustomTextField
          name="compressionEncryption"
          label={strings.compressionEncryption}
          defaultValue={info.compressionEncryption}
          inputRef={register}
        />
        <CustomSelect
          name="authentication"
          label={strings.authentication}
          defaultValue={info.authentication}
          inputRef={register}
        >
          <option>None</option>
          <option>Basic</option>
          <option>Digest</option>
        </CustomSelect>
        <CustomSelect
          name="fee"
          label={strings.fee}
          defaultValue={info.fee}
          inputRef={register}
        >
          <option>NO</option>
          <option>YES</option>
        </CustomSelect>
        <CustomTextField
          name="bitRate"
          label={strings.bitRate}
          defaultValue={info.bitRate}
          inputRef={register}
        />
        <CustomTextField
          name="miscellaneous"
          label={strings.miscellaneous}
          defaultValue={info.miscellaneous}
          inputRef={register}
        />
      </div>
      <div className={classes.submitSection}>
        <Button
          type="submit"
          variant="outlined"
          color="primary"
          disabled={submitState === SubmitState.Submitting}
        >
          {strings.apply}
        </Button>
        <ConfirmButton
          type="button"
          variant="outlined"
          color="secondary"
          disabled={submitState === SubmitState.Submitting}
          onClick={handleDelete}
          text={strings.delete}
        />
        <Typography variant="body2">
          {submitState === SubmitState.Submitting && strings.applying}
          {submitState === SubmitState.Error && strings.error}
        </Typography>
      </div>
    </form>
  )
}

function CustomTextField(props: TextFieldProps) {
  return (
    <TextField
      margin="dense"
      autoComplete="off"
      variant="outlined"
      fullWidth
      {...props}
    />
  )
}

function CustomSelect({ children, ...rest }: TextFieldProps) {
  const randomId = useMemo(() => 'rand' + Math.random(), [])
  return (
    <>
      <CustomTextField
        style={{ direction: 'ltr' }}
        inputProps={{ list: randomId }}
        {...rest}
      />
      <datalist id={randomId}>{children}</datalist>
    </>
  )
}

function SourceTypeSelect(props: TextFieldProps) {
  return (
    <CustomTextField select SelectProps={{ native: true }} {...props}>
      <option value="server">{strings.mountpointSourceServer}</option>
      <option value="nearest">{strings.mountpointSourceNearest}</option>
      <option value="external">{strings.mountpointSourceExternal}</option>
      <option value="proxy">{strings.proxy}</option>
    </CustomTextField>
  )
}

function InactiveSelect(props: TextFieldProps) {
  return (
    <CustomTextField select SelectProps={{ native: true }} {...props}>
      <option value="false">{strings.active}</option>
      <option value="true">{strings.inactive}</option>
    </CustomTextField>
  )
}

const useStyles = makeStyles((theme) => ({
  section1: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gridGap: theme.spacing(0.5, 1.5),
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  sectionTitle1: {
    margin: theme.spacing(0.5, 0, 1.5, 0),
  },
  sectionTitle: {
    margin: theme.spacing(4, 0, 1.5, 0),
  },
  fields: {
    paddingTop: 0,
    padding: theme.spacing(2),
  },
  submitSection: {
    flip: false,
    zIndex: 5,
    position: 'sticky',
    bottom: 0,
    padding: theme.spacing(1, 2),
    boxShadow: '0px -1px 1px 0px rgba(0, 0, 0, 0.1)',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    '& button': {
      flip: false,
      marginLeft: theme.spacing(2),
    },
  },
}))
